import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { FacadeService } from 'src/app/shared/services/facade.service';
import {
  ActivateKpiThread,
  ActivateThread,
  AskKpiQuestion,
  CreateSession,
  CreateSessionAndPostQuestion,
  LoadSessions,
  LoadUsage,
  NewChat,
  OpenSession,
  PostQuestion,
  RemoveConversation,
  TypeMessage,
} from '../actions/chatbot.actions';
import { ChatMessage, GENERAL_THREAD_ID, Sender } from '../models/chatbot.model';
import { ChatbotState } from '../reducers/chatbot.reducer';
import * as selectors from '../selectors/chatbot.selectors';

@Injectable({
  providedIn: 'root',
})
export class ChatbotFacadeService extends FacadeService {
  private rawTyping$ = new BehaviorSubject<ChatMessage[]>([]);
  private typingThreadId$ = new BehaviorSubject<string>('');
  private typingThreadId = '';
  private typingInProgress = false;
  private typingTimer: any = null;
  private currentTypingText = '';
  private typingTarget = '';
  private typingCompletes: Array<() => void> = [];

  constructor(public override store: Store<ChatbotState>) {
    super(store);
  }

  activeThreadId$ = this.storeSelect(selectors.getActiveThreadId);

  typing$ = combineLatest([this.rawTyping$, this.typingThreadId$, this.activeThreadId$]).pipe(
    map(([typing, typingThreadId, activeThreadId]) => (typingThreadId === activeThreadId ? typing : [])),
  );
  conversationList$ = this.storeSelect(selectors.getConversationList);
  pastSessionGroups$ = this.storeSelect(selectors.getPastSessionGroups);
  loadingSessions$ = this.storeSelect(selectors.getLoadingSessions);
  loadingHistory$ = this.storeSelect(selectors.getLoadingHistory);
  usage$ = this.storeSelect(selectors.getUsage);
  sessionId$ = this.storeSelect(selectors.getSessionId);
  message$ = this.storeSelect(selectors.getMessage);
  disableChatResponse$ = this.storeSelect(selectors.getDisableChat);
  messages$ = this.storeSelect(selectors.getMessages);
  showIntro$ = this.storeSelect(selectors.getShowIntro);
  showError$ = this.storeSelect(selectors.getShowError);

  get disableChat(): boolean {
    const chatDisabled = this.storeSnapshot(this.disableChatResponse$);
    return chatDisabled || this.typingInProgress;
  }

  get sessionId() {
    return this.storeSnapshot(this.sessionId$);
  }

  get activeThreadId() {
    return this.storeSnapshot(this.activeThreadId$);
  }

  get message() {
    return this.storeSnapshot(this.message$);
  }

  activateThread(threadId: string): void {
    this.dispatch(ActivateThread({ threadId }));
  }

  activateGeneralThread(): void {
    this.dispatch(ActivateThread({ threadId: GENERAL_THREAD_ID }));
  }

  newChat(): void {
    this.dispatch(NewChat());
  }

  removeConversation(threadId: string): void {
    this.dispatch(RemoveConversation({ threadId }));
  }

  loadSessions(): void {
    this.dispatch(LoadSessions());
  }

  loadUsage(): void {
    this.dispatch(LoadUsage());
  }

  openSession(sessionId: string): void {
    this.dispatch(OpenSession({ sessionId }));
  }

  activateKpiThread(kpiId: string, title: string): void {
    this.dispatch(ActivateKpiThread({ kpiId, title }));
  }

  askKpiQuestion(kpiId: string, title: string, message: string): void {
    this.dispatch(AskKpiQuestion({ kpiId, title, message }));
  }

  typeMessage(message: string): void {
    this.dispatch(TypeMessage({ message }));
  }

  createSession(): void {
    this.dispatch(CreateSession({ threadId: this.activeThreadId }));
  }

  postQuestion(message: string): void {
    this.dispatch(PostQuestion({ threadId: this.activeThreadId, message }));
  }

  createSessionAndPostQuestion(): void {
    this.dispatch(CreateSessionAndPostQuestion({ threadId: this.activeThreadId }));
  }

  startTyping(threadId: string): void {
    this.clearTypingTimer();
    this.typingInProgress = true;
    this.currentTypingText = '';
    this.typingThreadId = threadId;
    this.typingThreadId$.next(threadId);
  }

  updateTypingText(text: string): void {
    if (!this.typingInProgress) {
      this.startTyping(this.typingThreadId);
    }

    this.typingTarget = text;

    if (text.length <= this.currentTypingText.length) {
      this.currentTypingText = text;
      this.rawTyping$.next([{ sender: Sender.BOT, text: this.currentTypingText }]);
      this.resolveTypingCompletesIfNeeded();
      return;
    }

    const remaining = text.slice(this.currentTypingText.length);
    this.clearTypingTimer();

    const speed = 1;
    let idx = 0;
    this.typingTimer = setInterval(() => {
      this.currentTypingText += remaining[idx++] || '';
      this.rawTyping$.next([{ sender: Sender.BOT, text: this.currentTypingText }]);
      if (idx >= remaining.length) {
        this.clearTypingTimer();
        this.resolveTypingCompletesIfNeeded();
      }
    }, speed);
  }

  stopTyping(): void {
    this.clearTypingTimer(true);
    this.typingInProgress = false;
    this.currentTypingText = '';
    this.typingTarget = '';
    this.typingThreadId = '';
    this.rawTyping$.next([]);
    this.typingThreadId$.next('');
  }

  private clearTypingTimer(force = false): void {
    if (this.typingTimer) {
      clearInterval(this.typingTimer);
      this.typingTimer = null;
    }
    this.resolveTypingCompletesIfNeeded(force);
  }

  private resolveTypingCompletesIfNeeded(force = false): void {
    if (this.typingCompletes.length === 0) return;
    if (!force && this.currentTypingText !== this.typingTarget) return;

    while (this.typingCompletes.length) {
      const res = this.typingCompletes.shift();
      try {
        res && res();
      } catch (e) {}
    }
  }

  waitForTypingToFinish(text: string): Promise<void> {
    this.updateTypingText(text);
    if (this.currentTypingText === text) return Promise.resolve();
    return new Promise((resolve) => this.typingCompletes.push(resolve));
  }
}
